// Renders a release's `release_items[]` grouped by category, matching the
// old Timeline aesthetic: title per category, then a bulleted list with
// text (linear-linked), plus a meta line for parity / flags / tickets.
//
// Each item gets a derived GA/EA badge: no linked flags → GA, else EA if
// any linked flag isn't fully rolled out. Derivation lives in
// lib/flagRollout.ts (`derivedItemRollout`); this component just resolves
// the flag lookup once from useFlags().
import { useMemo } from "react";
import type { FeatureFlag, ItemCategory, ReleaseItem } from "@release-hub/shared";
import { useFlags } from "@/api/queries";
import { derivedItemRollout } from "@/lib/flagRollout";
import { FlagChip } from "./FlagChip";
import { renderTextWithLinearLinks } from "./LinearTicketLink";
import { StateChip } from "./StateChip";
import { WorkspaceHoverWrap } from "./WorkspaceHoverWrap";

const CATEGORY_ORDER: ItemCategory[] = [
  "feature",
  "enhancement",
  "bugfix",
  "maintenance",
];

const CATEGORY_LABELS: Record<ItemCategory, string> = {
  feature: "New features",
  enhancement: "Enhancements",
  bugfix: "Bugfixes",
  maintenance: "Maintenance",
};

const CATEGORY_TAG_CLASS: Record<ItemCategory, string> = {
  feature: "bg-major-soft text-major",
  enhancement: "bg-minor-soft text-minor",
  bugfix: "bg-ea-soft text-ea",
  maintenance: "bg-nightly-soft text-nightly",
};

export function ItemList({ items }: { items: ReleaseItem[] }) {
  const { data: flags } = useFlags();
  const flagsById = useMemo(() => {
    const map = new Map<string, FeatureFlag>();
    for (const f of flags ?? []) map.set(f.id, f);
    return map;
  }, [flags]);

  const buckets = new Map<ItemCategory, ReleaseItem[]>();
  for (const it of items) {
    const cat = it.category;
    if (!buckets.has(cat)) buckets.set(cat, []);
    buckets.get(cat)!.push(it);
  }
  return (
    <div className="space-y-5">
      {CATEGORY_ORDER.map((cat) => {
        const rows = buckets.get(cat);
        if (!rows || rows.length === 0) return null;
        return (
          <div key={cat}>
            <h5 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
              {CATEGORY_LABELS[cat]}
            </h5>
            <ul className="space-y-2">
              {rows.map((it) => {
                const roll = derivedItemRollout(it, flagsById);
                const itemFlags = it.linked_flag_ids
                  .map((id) => flagsById.get(id))
                  .filter((f): f is FeatureFlag => Boolean(f));
                return (
                  <li key={it.id} className="flex flex-col gap-1">
                    <div className="flex items-baseline gap-2">
                      <WorkspaceHoverWrap flags={itemFlags} title={it.text || "Release item"}>
                        <StateChip kind={roll} size="xs" />
                      </WorkspaceHoverWrap>
                      <span
                        className={`rounded-full px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider ${CATEGORY_TAG_CLASS[cat]}`}
                      >
                        {cat}
                      </span>
                      <span>{renderTextWithLinearLinks(it.text)}</span>
                    </div>
                    <ItemMeta item={it} />
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function ItemMeta({ item }: { item: ReleaseItem }) {
  const parts: React.ReactNode[] = [];
  if (item.parity_notes) parts.push(<span>{item.parity_notes}</span>);
  for (const fid of item.linked_flag_ids ?? []) {
    parts.push(<FlagChip flagId={fid} />);
  }
  for (const t of item.linear_ticket_ids ?? []) {
    parts.push(
      <a
        href={`https://linear.app/squint/issue/${t}`}
        target="_blank"
        rel="noopener noreferrer"
        className="content-link"
      >
        {t}
      </a>,
    );
  }
  if (parts.length === 0) return null;
  return (
    <div className="ml-6 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-secondary">
      {parts.map((p, i) => (
        <span key={i} className="inline-flex items-center gap-2">
          {i > 0 && <span className="text-text-tertiary">·</span>}
          {p}
        </span>
      ))}
    </div>
  );
}
